import axios from "axios";

function isLocalHostname(hostname) {
  return ["localhost", "127.0.0.1", "0.0.0.0"].includes(
    String(hostname || "").trim().toLowerCase()
  );
}

function resolveApiBaseUrl() {
  const configuredBaseUrl = String(
    process.env.REACT_APP_API_BASE_URL || ""
  ).trim();

  if (typeof window === "undefined") {
    return configuredBaseUrl || "http://localhost:8787";
  }

  const currentHostname = window.location.hostname;
  const isProductionBrowser =
    process.env.NODE_ENV === "production" && !isLocalHostname(currentHostname);

  if (isProductionBrowser) {
    // Use same-origin `/api` in production so session cookies stay first-party.
    return "";
  }

  return configuredBaseUrl || "http://localhost:8787";
}

const API_BASE_URL = resolveApiBaseUrl();
const FALLBACK_SCHOOL_ID = process.env.REACT_APP_SCHOOL_ID || "main";
const SCHOOL_ID_STORAGE_KEY = "swimmy_active_school_id";
const SCHOOL_ID_PATTERN = /^[a-z0-9_-]+$/i;
const activeSchoolSubscribers = new Set();
let storageListenerRegistered = false;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  withCredentials: true,
});

const tenantBase = () => "/api";

const isValidSchoolId = (schoolId) =>
  SCHOOL_ID_PATTERN.test(String(schoolId || "").trim());

const readActiveSchoolId = () => {
  if (typeof window === "undefined") {
    return FALLBACK_SCHOOL_ID;
  }

  const fromStorage = String(
    window.localStorage.getItem(SCHOOL_ID_STORAGE_KEY) || ""
  ).trim();

  if (isValidSchoolId(fromStorage)) {
    return fromStorage;
  }

  if (fromStorage) {
    window.localStorage.setItem(SCHOOL_ID_STORAGE_KEY, FALLBACK_SCHOOL_ID);
  }

  return FALLBACK_SCHOOL_ID;
};

const writeActiveSchoolId = (schoolId) => {
  const normalized = (schoolId || "").trim();
  if (!isValidSchoolId(normalized)) {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(SCHOOL_ID_STORAGE_KEY, FALLBACK_SCHOOL_ID);
    }
    return FALLBACK_SCHOOL_ID;
  }

  if (typeof window !== "undefined") {
    window.localStorage.setItem(SCHOOL_ID_STORAGE_KEY, normalized);
  }
  return normalized;
};

const notifyActiveSchoolSubscribers = () => {
  for (const listener of activeSchoolSubscribers) {
    listener();
  }
};

const ensureActiveSchoolStorageListener = () => {
  if (storageListenerRegistered || typeof window === "undefined") {
    return;
  }

  window.addEventListener("storage", (event) => {
    if (event.key === SCHOOL_ID_STORAGE_KEY) {
      notifyActiveSchoolSubscribers();
    }
  });
  storageListenerRegistered = true;
};

const EMPTY_USER_CONTEXT = { role: "mentor", name: "", loginId: "", schoolId: "" };
let currentUserContext = { ...EMPTY_USER_CONTEXT };

const normalizeCurrentUserContext = (user = {}) => ({
  role: user?.role || "mentor",
  name: user?.name || "",
  loginId: user?.loginId || "",
  schoolId: user?.schoolId || "",
});

const readCurrentUser = () => currentUserContext;

const requestHeaders = () => {
  return {
    "x-school-id": readActiveSchoolId(),
  };
};

const authRequestConfig = (schoolId) => ({
  headers: {
    "x-school-id": schoolId || readActiveSchoolId(),
  },
  withCredentials: true,
});

const CACHE_TTL = {
  schools: 5 * 60 * 1000,
  users: 60 * 1000,
  students: 60 * 1000,
  classRecords: 20 * 1000,
  commentTemplates: 5 * 60 * 1000,
  mentors: 60 * 1000,
  courses: 5 * 60 * 1000,
  curricula: 5 * 60 * 1000,
  studentMemos: 30 * 1000,
  googleIntegrations: 15 * 1000,
};

const responseCache = new Map();
const inflightRequests = new Map();
let cacheEpoch = 0;

const cloneCacheValue = (value) => {
  if (value === null || value === undefined) return value;
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value));
};

const getCacheContext = () => {
  const user = readCurrentUser();
  return `${readActiveSchoolId()}|${user.role}|${user.loginId || user.name || ""}`;
};

const buildCacheKey = (resource, params = {}) => {
  const sortedParams = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .sort(([a], [b]) => a.localeCompare(b));
  const query = sortedParams
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join("&");
  return `${getCacheContext()}|${resource}${query ? `?${query}` : ""}`;
};

const getCachedData = async (resource, params, ttlMs, loader) => {
  const key = buildCacheKey(resource, params);
  const now = Date.now();
  const cached = responseCache.get(key);

  if (cached && cached.expiresAt > now) {
    return cloneCacheValue(cached.value);
  }

  if (inflightRequests.has(key)) {
    const inflight = await inflightRequests.get(key);
    return cloneCacheValue(inflight);
  }

  const startEpoch = cacheEpoch;
  const requestPromise = loader()
    .then((value) => {
      if (cacheEpoch === startEpoch) {
        responseCache.set(key, { value, expiresAt: now + ttlMs });
      }
      return value;
    })
    .finally(() => {
      inflightRequests.delete(key);
    });

  inflightRequests.set(key, requestPromise);
  const value = await requestPromise;
  return cloneCacheValue(value);
};

const clearCache = (resourceNames = null) => {
  cacheEpoch += 1;

  if (!resourceNames || resourceNames.length === 0) {
    responseCache.clear();
    inflightRequests.clear();
    return;
  }

  const targets = resourceNames.map((name) => `|${name}`);

  for (const key of responseCache.keys()) {
    if (targets.some((target) => key.includes(target))) {
      responseCache.delete(key);
    }
  }

  for (const key of inflightRequests.keys()) {
    if (targets.some((target) => key.includes(target))) {
      inflightRequests.delete(key);
    }
  }
};

const toStringId = (value) =>
  value === null || value === undefined ? "" : String(value);

const normalizeStudent = (student) => ({
  ...student,
  id: toStringId(student.id),
  schoolId: student.schoolId || FALLBACK_SCHOOL_ID,
  schoolName: student.schoolName || student.schoolId || FALLBACK_SCHOOL_ID,
  age: student.age === null || student.age === undefined ? "" : Number(student.age),
  course: student.course || "",
  googleDriveFolder: student.googleDriveFolder || "",
});

const normalizeMentor = (mentor) => ({
  ...mentor,
  id: toStringId(mentor.id),
  name: mentor.name || mentor.lastName || "",
  lastName: mentor.lastName || "",
  firstName: mentor.firstName || "",
  emergencyContact: mentor.emergencyContact || "",
  status: mentor.status || "active",
});

const normalizeCourse = (course) => ({
  ...course,
  id: toStringId(course.id),
  order: Number(course.order || 0),
  description: course.description || "",
  isFixed: Boolean(course.isFixed),
});

const normalizeCurriculum = (curriculum) => ({
  ...curriculum,
  id: toStringId(curriculum.id),
  courseId: toStringId(curriculum.courseId),
  order: Number(curriculum.order || 0),
  description: curriculum.description || "",
});

const normalizeClassRecord = (record) => ({
  ...record,
  id: toStringId(record.id),
  studentId: toStringId(record.studentId),
  courseId: toStringId(record.courseId || ""),
  curriculumId: toStringId(record.curriculumId || ""),
  classPeriod: record.classPeriod || "",
  writingType: Array.isArray(record.writingType) ? record.writingType : [],
  images: Array.isArray(record.images) ? record.images : [],
  driveUploaded: Boolean(record.driveUploaded),
  typingComment: record.typingComment || "",
  curriculumComment: record.curriculumComment || "",
  nextClassRange: record.nextClassRange || "",
  deviceType: record.deviceType || "",
  deviceNumber: record.deviceNumber || "",
  programName: record.programName || "",
});

const normalizeTemplate = (template) => ({
  ...template,
  id: toStringId(template.id),
});

const normalizeMemo = (memo) => ({
  ...memo,
  id: toStringId(memo.id),
  studentId: toStringId(memo.studentId),
});

const normalizeSchool = (school) => ({
  ...school,
  id: toStringId(school.id),
  name: school.name || "",
  driveRootPath: school.driveRootPath || "生徒管理",
});

const normalizeUserAccount = (user) => ({
  ...user,
  id: toStringId(user.id),
  loginId: user.loginId || "",
  name: user.name || "",
  role: user.role || "mentor",
  status: user.status || "active",
  schoolId: user.schoolId || FALLBACK_SCHOOL_ID,
  schoolName: user.schoolName || user.schoolId || FALLBACK_SCHOOL_ID,
  mustChangePassword: Boolean(user.mustChangePassword),
});

const normalizeGoogleIntegration = (integration) => ({
  schoolId: integration.schoolId || "",
  schoolName: integration.schoolName || integration.schoolId || "",
  driveRootPath: integration.driveRootPath || "生徒管理",
  status: integration.status || "disconnected",
  googleEmail: integration.googleEmail || "",
  connected:
    "connected" in integration
      ? Boolean(integration.connected)
      : integration.status === "connected",
  lastConnectedAt: integration.lastConnectedAt || null,
  lastError: integration.lastError || "",
});

const buildTypingResult = (recordData) => {
  if (recordData.typingResult) return recordData.typingResult;
  if (!recordData.typingGrade) return "";

  return JSON.stringify({
    grade: recordData.typingGrade,
    data: recordData.typingData || {
      basicData: { charCount: "", accuracy: "", clearCount: "" },
      advancedData: [],
      mockTestStatus: "",
    },
  });
};

const toClassRecordPayload = (recordData) => ({
  studentId: toStringId(recordData.studentId),
  studentName: recordData.studentName || "",
  date: recordData.date,
  classPeriod: recordData.classPeriod || "",
  courseId: toStringId(recordData.courseId || ""),
  curriculumId: toStringId(recordData.curriculumId || ""),
  classRange: recordData.classRange || "",
  typingResult: buildTypingResult(recordData),
  writingStep: recordData.writingStep || "",
  writingCase: recordData.writingCase || "",
  writingType: recordData.writingType || [],
  typingComment: recordData.typingComment || "",
  curriculumComment: recordData.curriculumComment || "",
  nextClassRange: recordData.nextClassRange || "",
  deviceType: recordData.deviceType || "",
  deviceNumber: recordData.deviceNumber || "",
  driveUploaded: Boolean(recordData.driveUploaded),
  programName: recordData.programName || "",
  instructor: recordData.instructor || "",
  images: recordData.images || [],
});

const getRequestConfig = (params) => ({
  headers: requestHeaders(),
  params,
  withCredentials: true,
});

const resolveDefaultTenantScope = (options = {}) => {
  if (options.scope) return options.scope;
  const user = readCurrentUser();
  if (user.role === "admin" && readActiveSchoolId() === "main") {
    return "all-schools";
  }
  return undefined;
};

export const getActiveSchoolId = () => readActiveSchoolId();

export const setActiveSchoolId = (schoolId) => {
  const previousSchoolId = readActiveSchoolId();
  clearCache();
  const nextSchoolId = writeActiveSchoolId(schoolId);
  if (nextSchoolId !== previousSchoolId) {
    notifyActiveSchoolSubscribers();
  }
  return nextSchoolId;
};

export const subscribeActiveSchoolId = (listener) => {
  ensureActiveSchoolStorageListener();
  activeSchoolSubscribers.add(listener);
  return () => {
    activeSchoolSubscribers.delete(listener);
  };
};

export const getCurrentUserContext = () => readCurrentUser();

export const setCurrentUserContext = (user) => {
  currentUserContext = normalizeCurrentUserContext(user);
  return currentUserContext;
};

export const clearCurrentUserContext = () => {
  currentUserContext = { ...EMPTY_USER_CONTEXT };
};

export const loginWithLocalAccount = async ({ loginId, password, schoolId }) => {
  await api.post(
    `${tenantBase()}/auth/login`,
    { loginId, password },
    authRequestConfig(schoolId)
  );
  clearCache();

  try {
    const sessionResponse = await api.get(
      `${tenantBase()}/auth/session`,
      authRequestConfig(schoolId)
    );
    return normalizeUserAccount(sessionResponse.data.user);
  } catch (error) {
    const sessionError = new Error("session not established");
    sessionError.code = "session_not_established";
    sessionError.response = error?.response;
    sessionError.cause = error;
    throw sessionError;
  }
};

export const getSession = async () => {
  const response = await api.get(
    `${tenantBase()}/auth/session`,
    authRequestConfig()
  );
  return normalizeUserAccount(response.data.user);
};

export const logout = async () => {
  const response = await api.post(
    `${tenantBase()}/auth/logout`,
    {},
    authRequestConfig()
  );
  clearCache();
  return response.data;
};

export const changeOwnPassword = async ({
  currentPassword,
  newPassword,
  schoolId,
}) => {
  const response = await api.post(
    `${tenantBase()}/auth/change-password`,
    { currentPassword, newPassword },
    authRequestConfig(schoolId)
  );
  clearCache(["users"]);
  return normalizeUserAccount(response.data.user);
};

export const getSchools = async () => {
  return getCachedData("schools", {}, CACHE_TTL.schools, async () => {
    try {
      const response = await api.get(`${tenantBase()}/schools`, getRequestConfig());
      return (response.data.schools || []).map(normalizeSchool);
    } catch (error) {
      const status = error?.response?.status;
      const message = String(error?.response?.data?.error || "");

      if (status === 400 && message.includes("x-school-id")) {
        writeActiveSchoolId(FALLBACK_SCHOOL_ID);
        clearCache();
        const retry = await api.get(`${tenantBase()}/schools`, getRequestConfig());
        return (retry.data.schools || []).map(normalizeSchool);
      }

      throw error;
    }
  });
};

export const addSchool = async (schoolData) => {
  const response = await api.post(
    `${tenantBase()}/schools`,
    {
      id: schoolData.id,
      name: schoolData.name,
      driveRootPath: schoolData.driveRootPath || "生徒管理",
    },
    getRequestConfig()
  );
  clearCache(["schools", "students", "googleIntegrations"]);
  return normalizeSchool(response.data.school);
};

export const updateSchool = async (id, schoolData) => {
  const response = await api.put(
    `${tenantBase()}/schools/${encodeURIComponent(toStringId(id))}`,
    {
      name: schoolData.name,
      ...(schoolData.driveRootPath
        ? { driveRootPath: schoolData.driveRootPath }
        : {}),
    },
    getRequestConfig()
  );
  clearCache(["schools", "students", "googleIntegrations"]);
  return normalizeSchool(response.data.school);
};

export const deleteSchool = async (id) => {
  await api.delete(
    `${tenantBase()}/schools/${encodeURIComponent(toStringId(id))}`,
    getRequestConfig()
  );
  clearCache(["schools", "students", "users", "googleIntegrations"]);
  return true;
};

// User accounts
export const getUsers = async (options = {}) => {
  const params = options.scope ? { scope: options.scope } : {};
  return getCachedData("users", params, CACHE_TTL.users, async () => {
    const response = await api.get(
      `${tenantBase()}/users`,
      getRequestConfig(Object.keys(params).length > 0 ? params : undefined)
    );
    return (response.data.users || []).map(normalizeUserAccount);
  });
};

export const addUser = async (userData) => {
  const response = await api.post(
    `${tenantBase()}/users`,
    {
      loginId: userData.loginId,
      displayName: userData.displayName,
      role: userData.role,
      schoolId: userData.schoolId,
      status: userData.status || "active",
      temporaryPassword: userData.temporaryPassword,
    },
    getRequestConfig()
  );
  clearCache(["users"]);
  return normalizeUserAccount(response.data.user);
};

export const updateUser = async (id, userData) => {
  const response = await api.put(
    `${tenantBase()}/users/${encodeURIComponent(toStringId(id))}`,
    {
      ...("displayName" in userData ? { displayName: userData.displayName } : {}),
      ...("role" in userData ? { role: userData.role } : {}),
      ...("schoolId" in userData ? { schoolId: userData.schoolId } : {}),
      ...("status" in userData ? { status: userData.status } : {}),
    },
    getRequestConfig()
  );
  clearCache(["users"]);
  return normalizeUserAccount(response.data.user);
};

export const resetUserPassword = async (id, temporaryPassword) => {
  const response = await api.post(
    `${tenantBase()}/users/${encodeURIComponent(toStringId(id))}/reset-password`,
    { temporaryPassword },
    getRequestConfig()
  );
  clearCache(["users"]);
  return normalizeUserAccount(response.data.user);
};

export const deleteUser = async (id) => {
  await api.delete(
    `${tenantBase()}/users/${encodeURIComponent(toStringId(id))}`,
    getRequestConfig()
  );
  clearCache(["users"]);
  return true;
};

// School google integrations
export const getGoogleIntegrations = async () => {
  return getCachedData("googleIntegrations", {}, CACHE_TTL.googleIntegrations, async () => {
    const response = await api.get(
      `${tenantBase()}/google-integrations`,
      getRequestConfig()
    );
    return (response.data.integrations || []).map(normalizeGoogleIntegration);
  });
};

export const getGoogleIntegrationForActiveSchool = async () => {
  return getCachedData(
    "googleIntegrations",
    { activeSchool: true },
    CACHE_TTL.googleIntegrations,
    async () => {
      const response = await api.get(
        `${tenantBase()}/google-integration`,
        getRequestConfig()
      );
      return normalizeGoogleIntegration(response.data || {});
    }
  );
};

export const linkSchoolGoogleIntegration = async (schoolId, code) => {
  const response = await api.post(
    `${tenantBase()}/schools/${encodeURIComponent(
      toStringId(schoolId)
    )}/google-integration/link`,
    { code },
    getRequestConfig()
  );
  clearCache(["googleIntegrations"]);
  return normalizeGoogleIntegration(response.data.integration || {});
};

export const unlinkSchoolGoogleIntegration = async (schoolId) => {
  const response = await api.delete(
    `${tenantBase()}/schools/${encodeURIComponent(
      toStringId(schoolId)
    )}/google-integration`,
    getRequestConfig()
  );
  clearCache(["googleIntegrations"]);
  return Boolean(response.data?.ok);
};

export const uploadToLinkedSchoolGoogleDrive = async (payload) => {
  const response = await api.post(
    `${tenantBase()}/google-integration/upload`,
    payload,
    getRequestConfig()
  );
  clearCache(["classRecords"]);
  return response.data;
};

// Students
export const getStudents = async (options = {}) => {
  const params = options.scope ? { scope: options.scope } : {};
  return getCachedData("students", params, CACHE_TTL.students, async () => {
    const response = await api.get(
      `${tenantBase()}/students`,
      getRequestConfig(Object.keys(params).length > 0 ? params : undefined)
    );
    return (response.data.students || []).map(normalizeStudent);
  });
};

export const getStudent = async (id) => {
  const students = await getStudents();
  return students.find((student) => student.id === toStringId(id));
};

export const addStudent = async (studentData, options = {}) => {
  const payload = {
    name: studentData.name,
    age: studentData.age === "" ? null : Number(studentData.age),
    course: studentData.course || "",
    googleDriveFolder: studentData.googleDriveFolder || "",
    status: studentData.status || "active",
    ...(studentData.schoolId ? { schoolId: studentData.schoolId } : {}),
  };
  const params = options.scope ? { scope: options.scope } : undefined;
  const response = await api.post(
    `${tenantBase()}/students`,
    payload,
    getRequestConfig(params)
  );
  clearCache(["students", "classRecords", "studentMemos"]);
  return normalizeStudent(response.data.student);
};

export const updateStudent = async (id, studentData, options = {}) => {
  const payload = {
    name: studentData.name,
    age: studentData.age === "" ? null : Number(studentData.age),
    course: studentData.course || "",
    googleDriveFolder: studentData.googleDriveFolder || "",
    status: studentData.status || "active",
    ...(studentData.schoolId ? { schoolId: studentData.schoolId } : {}),
  };
  const params = options.scope ? { scope: options.scope } : undefined;
  const response = await api.put(
    `${tenantBase()}/students/${encodeURIComponent(toStringId(id))}`,
    payload,
    getRequestConfig(params)
  );
  clearCache(["students", "classRecords", "studentMemos"]);
  return normalizeStudent(response.data.student);
};

export const deleteStudent = async (id, options = {}) => {
  const params = options.scope ? { scope: options.scope } : undefined;
  await api.delete(
    `${tenantBase()}/students/${encodeURIComponent(toStringId(id))}`,
    getRequestConfig(params)
  );
  clearCache(["students", "classRecords", "studentMemos"]);
  return true;
};

export const searchStudents = async (query, options = {}) => {
  const params = {
    q: query,
    ...(options.scope ? { scope: options.scope } : {}),
  };
  const response = await api.get(
    `${tenantBase()}/students`,
    getRequestConfig(params)
  );
  return (response.data.students || []).map(normalizeStudent);
};

// Class records
export const getClassRecords = async (studentId = null, options = {}) => {
  const scope = resolveDefaultTenantScope(options);
  const params = {
    ...(studentId ? { studentId: toStringId(studentId) } : {}),
    ...(scope ? { scope } : {}),
  };
  return getCachedData("classRecords", params, CACHE_TTL.classRecords, async () => {
    const response = await api.get(
      `${tenantBase()}/class-records`,
      getRequestConfig(Object.keys(params).length > 0 ? params : undefined)
    );
    return (response.data.classRecords || []).map(normalizeClassRecord);
  });
};

export const getRecentClassRecords = async (limit = 10, options = {}) => {
  const scope = resolveDefaultTenantScope(options);
  const params = {
    limit: Number(limit) || 10,
    ...(scope ? { scope } : {}),
  };
  return getCachedData(
    "classRecords",
    params,
    CACHE_TTL.classRecords,
    async () => {
      const response = await api.get(
        `${tenantBase()}/class-records`,
        getRequestConfig({ limit, ...(scope ? { scope } : {}) })
      );
      return (response.data.classRecords || []).map(normalizeClassRecord);
    }
  );
};

export const addClassRecord = async (recordData) => {
  const response = await api.post(
    `${tenantBase()}/class-records`,
    toClassRecordPayload(recordData),
    getRequestConfig()
  );
  clearCache(["classRecords"]);
  return normalizeClassRecord(response.data.classRecord);
};

export const updateClassRecord = async (id, recordData) => {
  const response = await api.put(
    `${tenantBase()}/class-records/${encodeURIComponent(toStringId(id))}`,
    toClassRecordPayload(recordData),
    getRequestConfig()
  );
  clearCache(["classRecords"]);
  return normalizeClassRecord(response.data.classRecord);
};

export const markClassRecordDriveUploaded = async (id) => {
  const response = await api.post(
    `${tenantBase()}/class-records/${encodeURIComponent(
      toStringId(id)
    )}/drive-uploaded`,
    {},
    getRequestConfig()
  );
  clearCache(["classRecords"]);
  return normalizeClassRecord(response.data.classRecord);
};

export const deleteClassRecord = async (id) => {
  await api.delete(
    `${tenantBase()}/class-records/${encodeURIComponent(toStringId(id))}`,
    getRequestConfig()
  );
  clearCache(["classRecords"]);
  return true;
};

// Comment templates
export const getCommentTemplates = async () => {
  return getCachedData("commentTemplates", {}, CACHE_TTL.commentTemplates, async () => {
    const response = await api.get(
      `${tenantBase()}/comment-templates`,
      getRequestConfig()
    );
    return (response.data.templates || []).map(normalizeTemplate);
  });
};

export const addCommentTemplate = async (templateData) => {
  const response = await api.post(
    `${tenantBase()}/comment-templates`,
    {
      category: templateData.category,
      text: templateData.text,
    },
    getRequestConfig()
  );
  clearCache(["commentTemplates"]);
  return normalizeTemplate(response.data.template);
};

// Mentors
export const getMentors = async (options = {}) => {
  const params = options.scope ? { scope: options.scope } : {};
  return getCachedData("mentors", params, CACHE_TTL.mentors, async () => {
    const response = await api.get(
      `${tenantBase()}/mentors`,
      getRequestConfig(Object.keys(params).length > 0 ? params : undefined)
    );
    return (response.data.mentors || []).map(normalizeMentor);
  });
};

export const getMentor = async (id) => {
  const mentors = await getMentors();
  return mentors.find((mentor) => mentor.id === toStringId(id));
};

export const addMentor = async (mentorData) => {
  const response = await api.post(
    `${tenantBase()}/mentors`,
    {
      name: mentorData.name,
      lastName: mentorData.lastName || "",
      firstName: mentorData.firstName || "",
      emergencyContact: mentorData.emergencyContact || "",
      status: mentorData.status || "active",
      role: mentorData.role || "mentor",
    },
    getRequestConfig()
  );
  clearCache(["mentors"]);
  return normalizeMentor(response.data.mentor);
};

export const updateMentor = async (id, mentorData) => {
  const response = await api.put(
    `${tenantBase()}/mentors/${encodeURIComponent(toStringId(id))}`,
    {
      name: mentorData.name,
      lastName: mentorData.lastName || "",
      firstName: mentorData.firstName || "",
      emergencyContact: mentorData.emergencyContact || "",
      status: mentorData.status || "active",
      role: mentorData.role || "mentor",
    },
    getRequestConfig()
  );
  clearCache(["mentors"]);
  return normalizeMentor(response.data.mentor);
};

export const deleteMentor = async (id) => {
  await api.delete(
    `${tenantBase()}/mentors/${encodeURIComponent(toStringId(id))}`,
    getRequestConfig()
  );
  clearCache(["mentors"]);
  return true;
};

export const searchMentors = async (query) => {
  const response = await api.get(
    `${tenantBase()}/mentors`,
    getRequestConfig({ q: query })
  );
  return (response.data.mentors || []).map(normalizeMentor);
};

export const getLastTypingResult = async (
  studentId,
  excludeRecordId = null,
  options = {}
) => {
  const scope = resolveDefaultTenantScope(options);
  const params = {
    studentId: toStringId(studentId),
    ...(scope ? { scope } : {}),
  };
  if (excludeRecordId) params.excludeId = toStringId(excludeRecordId);

  const records = await getCachedData(
    "classRecords",
    params,
    CACHE_TTL.classRecords,
    async () => {
      const response = await api.get(
        `${tenantBase()}/class-records`,
        getRequestConfig(params)
      );
      return (response.data.classRecords || []).map(normalizeClassRecord);
    }
  );

  if (records.length === 0) {
    return {
      grade: "",
      data: {
        basicData: { charCount: "", accuracy: "", clearCount: "" },
        advancedData: [],
        mockTestStatus: "",
      },
    };
  }

  const recordWithTyping = records.find((record) => record.typingResult);
  if (!recordWithTyping) {
    return {
      grade: "",
      data: {
        basicData: { charCount: "", accuracy: "", clearCount: "" },
        advancedData: [],
        mockTestStatus: "",
      },
    };
  }

  try {
    const parsed = JSON.parse(recordWithTyping.typingResult);
    return {
      grade: parsed.grade || "",
      data: parsed.data || {
        basicData: { charCount: "", accuracy: "", clearCount: "" },
        advancedData: [],
        mockTestStatus: "",
      },
    };
  } catch {
    return {
      grade: "",
      data: {
        basicData: { charCount: "", accuracy: "", clearCount: "" },
        advancedData: [],
        mockTestStatus: "",
      },
    };
  }
};

// Courses / curricula
export const getCourses = async () => {
  return getCachedData("courses", {}, CACHE_TTL.courses, async () => {
    const response = await api.get(`${tenantBase()}/courses`, getRequestConfig());
    return (response.data.courses || []).map(normalizeCourse);
  });
};

export const getCourse = async (id) => {
  const courses = await getCourses();
  return courses.find((course) => course.id === toStringId(id));
};

export const addCourse = async (courseData) => {
  const response = await api.post(
    `${tenantBase()}/courses`,
    {
      name: courseData.name,
      description: courseData.description || "",
      order: Number(courseData.order || 0),
    },
    getRequestConfig()
  );
  clearCache(["courses", "curricula", "classRecords"]);
  return normalizeCourse(response.data.course);
};

export const updateCourse = async (id, courseData) => {
  const response = await api.put(
    `${tenantBase()}/courses/${encodeURIComponent(toStringId(id))}`,
    {
      ...("name" in courseData ? { name: courseData.name } : {}),
      ...("description" in courseData ? { description: courseData.description || "" } : {}),
      ...("order" in courseData ? { order: Number(courseData.order || 0) } : {}),
    },
    getRequestConfig()
  );
  clearCache(["courses", "curricula", "classRecords"]);
  return normalizeCourse(response.data.course);
};

export const deleteCourse = async (id) => {
  await api.delete(
    `${tenantBase()}/courses/${encodeURIComponent(toStringId(id))}`,
    getRequestConfig()
  );
  clearCache(["courses", "curricula", "classRecords"]);
  return true;
};

export const searchCourses = async (query) => {
  const response = await api.get(
    `${tenantBase()}/courses`,
    getRequestConfig({ q: query })
  );
  return (response.data.courses || []).map(normalizeCourse);
};

export const getCurricula = async (courseId = null) => {
  const params = courseId ? { courseId: toStringId(courseId) } : {};
  return getCachedData("curricula", params, CACHE_TTL.curricula, async () => {
    const response = await api.get(
      `${tenantBase()}/curricula`,
      getRequestConfig(Object.keys(params).length > 0 ? params : undefined)
    );
    return (response.data.curricula || []).map(normalizeCurriculum);
  });
};

export const addCurriculum = async (curriculumData) => {
  const response = await api.post(
    `${tenantBase()}/curricula`,
    {
      courseId: toStringId(curriculumData.courseId),
      title: curriculumData.title,
      description: curriculumData.description || "",
      order: Number(curriculumData.order || 0),
    },
    getRequestConfig()
  );
  clearCache(["curricula", "classRecords"]);
  return normalizeCurriculum(response.data.curriculum);
};

export const updateCurriculum = async (id, curriculumData) => {
  const response = await api.put(
    `${tenantBase()}/curricula/${encodeURIComponent(toStringId(id))}`,
    {
      ...("courseId" in curriculumData ? { courseId: toStringId(curriculumData.courseId) } : {}),
      ...("title" in curriculumData ? { title: curriculumData.title } : {}),
      ...("description" in curriculumData ? { description: curriculumData.description || "" } : {}),
      ...("order" in curriculumData ? { order: Number(curriculumData.order || 0) } : {}),
    },
    getRequestConfig()
  );
  clearCache(["curricula", "classRecords"]);
  return normalizeCurriculum(response.data.curriculum);
};

export const deleteCurriculum = async (id) => {
  await api.delete(
    `${tenantBase()}/curricula/${encodeURIComponent(toStringId(id))}`,
    getRequestConfig()
  );
  clearCache(["curricula", "classRecords"]);
  return true;
};

// Progress analytics
export const getStudentProgress = async (studentId, monthsBack = 6) => {
  try {
    const records = await getClassRecords(studentId);
    const student = await getStudent(studentId);
    const allClasses = await getCourses();
    const allCurricula = await getCurricula();

    if (!student) {
      return null;
    }

    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() - monthsBack);

    const recentRecords = records.filter((record) => new Date(record.date) >= cutoffDate);

    const curriculumProgress = calculateCurriculumProgress(
      recentRecords,
      allClasses,
      allCurricula
    );
    const topicDiversity = calculateTopicDiversity(recentRecords);
    const stats = calculateCurriculumStats(
      curriculumProgress,
      topicDiversity,
      recentRecords
    );

    return {
      studentId,
      studentName: student.name,
      studentCourse: student.course,
      totalRecords: recentRecords.length,
      curriculumProgress,
      topicDiversity,
      stats,
      recentRecords: recentRecords.slice(0, 5),
    };
  } catch (error) {
    console.error("Failed to get student progress", error);
    return null;
  }
};

const calculateCurriculumProgress = (records, allClasses, allCurricula) => {
  const studiedTopics = records.map((record) => record.classRange).filter(Boolean);
  const uniqueTopics = [...new Set(studiedTopics)];

  const classProgress = {};

  allClasses.forEach((cls) => {
    const classTopics = allCurricula.filter(
      (curriculum) => curriculum.courseId === cls.id
    );
    const studiedClassTopics = uniqueTopics.filter((topic) =>
      classTopics.some(
        (curriculum) => curriculum.title === topic || curriculum.name === topic
      )
    );

    classProgress[cls.name] = {
      courseId: cls.id,
      className: cls.name,
      description: cls.description,
      totalTopics: classTopics.length,
      studiedTopics: studiedClassTopics.length,
      completionRate:
        classTopics.length > 0
          ? Math.round((studiedClassTopics.length / classTopics.length) * 100)
          : 0,
      topics: classTopics.map((curriculum) => ({
        ...curriculum,
        studied: studiedClassTopics.includes(curriculum.title || curriculum.name),
      })),
    };
  });

  return {
    uniqueTopicsCount: uniqueTopics.length,
    totalStudySessions: studiedTopics.length,
    classProgress,
    studiedTopics: uniqueTopics,
  };
};

const calculateTopicDiversity = (records) => {
  const topicFrequency = {};
  const recentTopics = [];

  records.forEach((record) => {
    if (record.classRange) {
      topicFrequency[record.classRange] = (topicFrequency[record.classRange] || 0) + 1;
      recentTopics.push({ topic: record.classRange, date: record.date });
    }
  });

  const diversityScore =
    records.length > 0
      ? Math.round((Object.keys(topicFrequency).length / records.length) * 100)
      : 0;

  return {
    topicFrequency,
    recentTopics,
    diversityScore,
    mostStudiedTopic: Object.keys(topicFrequency).reduce(
      (a, b) => (topicFrequency[a] > topicFrequency[b] ? a : b),
      ""
    ),
  };
};

const calculateCurrentStreak = (records) => {
  if (records.length === 0) return 0;

  const sortedRecords = [...records].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );

  let streak = 0;
  let currentDate = new Date();

  for (const record of sortedRecords) {
    const recordDate = new Date(record.date);
    const daysDiff = Math.floor((currentDate - recordDate) / (24 * 60 * 60 * 1000));

    if (daysDiff <= 7 * (streak + 1)) {
      streak += 1;
      currentDate = recordDate;
    } else {
      break;
    }
  }

  return streak;
};

const calculateCurriculumStats = (curriculumProgress, topicDiversity, records) => {
  if (records.length === 0) {
    return {
      totalTopicsStudied: 0,
      averageCompletionRate: 0,
      topicDiversityScore: 0,
      attendanceRate: 0,
      currentStreak: 0,
      mostAdvancedClass: "",
      recentTopicCount: 0,
    };
  }

  const classProgressValues = Object.values(curriculumProgress.classProgress);
  const averageCompletionRate =
    classProgressValues.length > 0
      ? Math.round(
          classProgressValues.reduce((sum, cp) => sum + cp.completionRate, 0) /
            classProgressValues.length
        )
      : 0;

  const mostAdvancedClass = classProgressValues.reduce(
    (prev, current) =>
      current.completionRate > prev.completionRate ? current : prev,
    { className: "", completionRate: 0 }
  );

  const weeksInPeriod = Math.ceil(
    (new Date() - new Date(Math.min(...records.map((r) => new Date(r.date))))) /
      (7 * 24 * 60 * 60 * 1000)
  );
  const attendanceRate =
    weeksInPeriod > 0
      ? Math.min(100, Math.round((records.length / weeksInPeriod) * 100))
      : 100;

  const currentStreak = calculateCurrentStreak(records);

  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  const recentTopics = records
    .filter((record) => new Date(record.date) >= oneMonthAgo)
    .map((record) => record.classRange)
    .filter(Boolean);
  const recentTopicCount = new Set(recentTopics).size;

  return {
    totalTopicsStudied: curriculumProgress.uniqueTopicsCount,
    averageCompletionRate,
    topicDiversityScore: topicDiversity.diversityScore,
    attendanceRate,
    currentStreak,
    mostAdvancedClass: mostAdvancedClass.className,
    recentTopicCount,
  };
};

export const getAllStudentsProgressSummary = async () => {
  try {
    const students = await getStudents();
    const progressSummaries = [];

    for (const student of students) {
      const progress = await getStudentProgress(student.id, 3);
      if (progress) {
        progressSummaries.push({
          studentId: student.id,
          studentName: student.name,
          course: student.course,
          totalRecords: progress.totalRecords,
          totalTopicsStudied: progress.stats.totalTopicsStudied,
          averageCompletionRate: progress.stats.averageCompletionRate,
          topicDiversityScore: progress.stats.topicDiversityScore,
          attendanceRate: progress.stats.attendanceRate,
          currentStreak: progress.stats.currentStreak,
          mostAdvancedClass: progress.stats.mostAdvancedClass,
          recentTopicCount: progress.stats.recentTopicCount,
          lastRecordDate:
            progress.recentRecords.length > 0
              ? progress.recentRecords[0].date
              : null,
        });
      }
    }

    return progressSummaries;
  } catch (error) {
    console.error("Failed to get progress summary", error);
    return [];
  }
};

// Student memos
export const getStudentMemos = async (studentId) => {
  const params = { studentId: toStringId(studentId) };
  return getCachedData("studentMemos", params, CACHE_TTL.studentMemos, async () => {
    const response = await api.get(
      `${tenantBase()}/student-memos`,
      getRequestConfig(params)
    );
    return (response.data.memos || []).map(normalizeMemo);
  });
};

export const addStudentMemo = async (memoData) => {
  const response = await api.post(
    `${tenantBase()}/student-memos`,
    {
      studentId: toStringId(memoData.studentId),
      content: memoData.content,
      createdBy: memoData.createdBy || "",
    },
    getRequestConfig()
  );
  clearCache(["studentMemos"]);
  return normalizeMemo(response.data.memo);
};

export const updateStudentMemo = async (id, memoData) => {
  const response = await api.put(
    `${tenantBase()}/student-memos/${encodeURIComponent(toStringId(id))}`,
    {
      ...(memoData.studentId ? { studentId: toStringId(memoData.studentId) } : {}),
      ...("content" in memoData ? { content: memoData.content } : {}),
      ...("createdBy" in memoData ? { createdBy: memoData.createdBy || "" } : {}),
    },
    getRequestConfig()
  );
  clearCache(["studentMemos"]);
  return normalizeMemo(response.data.memo);
};

export const deleteStudentMemo = async (id) => {
  await api.delete(
    `${tenantBase()}/student-memos/${encodeURIComponent(toStringId(id))}`,
    getRequestConfig()
  );
  clearCache(["studentMemos"]);
  return true;
};
